import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { X, MessageSquare, Smartphone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Message Preview Modal Component
function MessageModal({ isOpen, onClose, message, receiver, date, status, senderNumber }) {
  if (!isOpen) return null;
  
  // Format phone number for display
  const formatPhone = (phone) => {
    if (!phone) return 'Unknown';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('91') && cleaned.length === 12) {
      return `+91 ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
    }
    return phone;
  };
  
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'white',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '80vh',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <MessageSquare size={18} color="white" />
            </div>
            <div>
              <h3 style={{ margin: 0, color: 'white', fontSize: '14px', fontWeight: '600' }}>
                Message Details
              </h3>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>
                To: {receiver}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={18} color="white" />
          </button>
        </div>
        
        {/* Content */}
        <div style={{ padding: '20px' }}>
          {/* Meta Info */}
          <div style={{
            display: 'flex',
            gap: '16px',
            marginBottom: '16px',
            paddingBottom: '16px',
            borderBottom: '1px solid #e2e8f0',
            flexWrap: 'wrap'
          }}>
            <div>
              <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '600' }}>Date & Time</p>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#334155' }}>{date}</p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '600' }}>Status</p>
              <span style={{
                display: 'inline-block',
                marginTop: '4px',
                padding: '2px 8px',
                background: status === 'sent' ? '#dcfce7' : '#fee2e2',
                color: status === 'sent' ? '#166534' : '#991b1b',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {status}
              </span>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '600' }}>Sent From</p>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#334155', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Smartphone size={12} style={{ color: '#667eea' }} />
                {formatPhone(senderNumber)}
              </p>
            </div>
          </div>
          
          {/* Message Content */}
          <div>
            <p style={{ margin: '0 0 8px', fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '600' }}>Message Content</p>
            <div style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '16px',
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
              <p style={{
                margin: 0,
                color: '#334155',
                fontSize: '14px',
                lineHeight: '1.7',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {message}
              </p>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div style={{
          padding: '12px 20px',
          background: '#f8fafc',
          borderTop: '1px solid #e2e8f0'
        }}>
          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: '10px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Message Cell - Clickable message text to open modal
function MessageCell({ message, receiver, date, status, senderNumber }) {
  const [showModal, setShowModal] = useState(false);
  
  if (!message) return <span style={{ color: '#94a3b8', fontSize: '14px' }}>-</span>;
  
  return (
    <>
      <span 
        onClick={() => setShowModal(true)}
        style={{ 
          display: 'inline-block',
          maxWidth: 'calc(100% + 16px)',
          cursor: 'pointer',
          padding: '4px 8px',
          marginLeft: '-8px',
          borderRadius: '6px',
          transition: 'all 0.2s ease',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: '#334155',
          fontSize: '14px'
        }}
        className="message-cell-hover"
        title="Click to view full message"
      >
        {message}
      </span>
      
      <MessageModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        message={message}
        receiver={receiver}
        date={date}
        status={status}
        senderNumber={senderNumber}
      />
    </>
  );
}

export default function MessageLogs({ user }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  // Format phone number for display
  const formatSenderPhone = (phone) => {
    if (!phone) return '-';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('91') && cleaned.length === 12) {
      return `+91 ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
    }
    return phone;
  };

  // Format receiver phone number (same format as sender)
  const formatReceiverPhone = (phone) => {
    if (!phone) return '-';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('91') && cleaned.length === 12) {
      return `+91 ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
    }
    if (cleaned.length === 10) {
      return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    }
    return phone;
  };

  useEffect(() => {
    fetchLogs();
    
    // Auto-refresh every 3 seconds
    const interval = setInterval(() => {
      fetchLogs();
    }, 3000);

    return () => clearInterval(interval);
  }, [filter]);

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }
      
      const url = filter === 'all' 
        ? `${API}/messages/logs` 
        : `${API}/messages/logs?status=${filter}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      } else {
        console.error('Error fetching logs:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  return (
    <Layout user={user}>
      <div className="page-header">
        <h1 data-testid="page-title">Message Logs</h1>
        <p>View your message history and delivery status</p>
      </div>

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3>Message History</h3>
            <p style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
              🔄 Auto-refreshing every 3 seconds
            </p>
          </div>
          <div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px'
              }}
              data-testid="filter-select"
            >
              <option value="all">All Messages</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            Loading logs...
          </div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }} data-testid="no-logs-message">
            No messages found
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table" data-testid="logs-table" style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: '14%', fontSize: '12px', fontWeight: '700' }}>Date & Time</th>
                  <th style={{ width: '12%', fontSize: '12px', fontWeight: '700' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Smartphone size={12} />
                      Sent From
                    </div>
                  </th>
                  <th style={{ width: '12%', fontSize: '12px', fontWeight: '700' }}>Receiver</th>
                  <th style={{ width: '37%', fontSize: '12px', fontWeight: '700' }}>Message</th>
                  <th style={{ width: '10%', fontSize: '12px', fontWeight: '700' }}>Status</th>
                  <th style={{ width: '10%', fontSize: '12px', fontWeight: '700' }}>Source</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td style={{ fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatDate(log.created_at)}</td>
                    <td style={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '2px 8px',
                        background: log.sender_number ? '#e0e7ff' : '#f1f5f9',
                        color: log.sender_number ? '#3730a3' : '#64748b',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {formatSenderPhone(log.sender_number)}
                      </span>
                    </td>
                    <td style={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '2px 8px',
                        background: '#dcfce7',
                        color: '#166534',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {formatReceiverPhone(log.receiver_number)}
                      </span>
                    </td>
                    <td style={{ overflow: 'hidden' }}>
                      <MessageCell 
                        message={log.message_body} 
                        receiver={log.receiver_number}
                        date={formatDate(log.created_at)}
                        status={log.status}
                        senderNumber={log.sender_number}
                      />
                    </td>
                    <td style={{ textAlign: 'left' }}>
                      <span style={{ 
                        display: 'inline-block',
                        padding: '4px 12px', 
                        background: log.status === 'sent' ? '#dcfce7' : log.status === 'pending' ? '#fef3c7' : '#fee2e2',
                        color: log.status === 'sent' ? '#166534' : log.status === 'pending' ? '#92400e' : '#991b1b',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {log.status}
                      </span>
                    </td>
                    <td style={{ textAlign: 'left' }}>
                      <span style={{ 
                        display: 'inline-block',
                        padding: '4px 12px', 
                        background: log.source === 'api' ? '#e0e7ff' : '#f3e8ff',
                        color: log.source === 'api' ? '#3730a3' : '#6b21a8',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {log.source.toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
